import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  // Changing this (e.g. to a new result) clears a previous error.
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

// Keeps a rendering bug in one panel from blanking the whole page.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Results panel failed to render:", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex items-start gap-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-lenovo-red" aria-hidden />
        <div className="text-sm text-red-800 dark:text-red-200">
          <p className="font-semibold">The results couldn't be displayed.</p>
          <p className="mt-1">
            This usually means the backend is out of date. Restart it and calculate again. Details:{" "}
            <code className="text-xs">{this.state.error.message}</code>
          </p>
        </div>
      </div>
    );
  }
}
